import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Avatar, Stack, Chip, IconButton, Tooltip, Button } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { getCreatedTasks, getTasks, updateTaskStatus } from '../../redux/features/taskManagementSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';

import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import BlockIcon from '@mui/icons-material/Block';
import ChecklistIcon from '@mui/icons-material/Checklist';
import EventIcon from '@mui/icons-material/Event';
import FlagIcon from '@mui/icons-material/Flag';
import { PersonAddAlt1Outlined, VisibilityOutlined } from '@mui/icons-material';
import { TaskAssignmentDialog, TaskDetailsDialog } from './TaskDialogs';
import dayjs from 'dayjs';

// ------------------ STATUS CONFIG ------------------
export const statusConfig = {
  pending_for_assignment: {
    id: -1,
    label: 'Pending For Assignment',
    icon: <AccessTimeIcon sx={{ fontSize: 15 }} />,
    bg: '#FFF7ED',
    color: '#C2410C',
    border: '#FDBA74',
    dot: '#F97316',
  },
  pending: {
    id: 0,
    label: 'Pending',
    icon: <AccessTimeIcon sx={{ fontSize: 15 }} />,
    bg: '#FFF7ED',
    color: '#C2410C',
    border: '#FDBA74',
    dot: '#F97316',
  },

  on_hold: {
    id: 1,
    label: 'On Hold',
    icon: <PauseCircleOutlineIcon sx={{ fontSize: 15 }} />,
    bg: '#FEF3C7',
    color: '#92400E',
    border: '#FCD34D',
    dot: '#F59E0B',
  },

  approved: {
    id: 2,
    label: 'Approved',
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />,
    bg: '#ECFDF5',
    color: '#065F46',
    border: '#6EE7B7',
    dot: '#10B981',
  },

  rejected: {
    id: 3,
    label: 'Rejected',
    icon: <CancelOutlinedIcon sx={{ fontSize: 15 }} />,
    bg: '#FEF2F2',
    color: '#991B1B',
    border: '#FCA5A5',
    dot: '#EF4444',
  },

  in_progress: {
    id: 4,
    label: 'In Progress',
    icon: <SyncIcon sx={{ fontSize: 15 }} />,
    bg: '#EFF6FF',
    color: '#1D4ED8',
    border: '#93C5FD',
    dot: '#3B82F6',
  },

  completed: {
    id: 5,
    label: 'Completed',
    icon: <TaskAltIcon sx={{ fontSize: 15 }} />,
    bg: '#F0FDF4',
    color: '#15803D',
    border: '#86EFAC',
    dot: '#22C55E',
  },

  cancelled: {
    id: 6,
    label: 'Cancelled',
    icon: <BlockIcon sx={{ fontSize: 15 }} />,
    bg: '#F3F4F6',
    color: '#374151',
    border: '#D1D5DB',
    dot: '#6B7280',
  },
};
export const statusById = Object.fromEntries(Object.values(statusConfig).map((item) => [item.id, item]));
const colors = ['#6366f1', '#0ea5e9', '#ec4899', '#22c55e', '#f59e0b', '#ef4444'];

const getRandomColor = (index) => colors[index % colors.length];

const DUE_SOON_WINDOW_DAYS = 3;

// Traffic-light urgency for a pending task's deadline: red once it's overdue,
// amber inside the due-soon window, green otherwise. A completed task's
// deadline no longer carries urgency, so it's shown neutral/gray.
const getDeadlineUrgency = (dateStr, assignmentStatus) => {
  if (!dateStr) return { label: 'No due date', color: '#94A3B8', bg: '#F1F5F9', border: '#E2E8F0' };
  if (assignmentStatus === 5) return { label: 'Completed', color: '#64748B', bg: '#F1F5F9', border: '#E2E8F0' };

  const daysLeft = dayjs(dateStr).startOf('day').diff(dayjs().startOf('day'), 'day');

  if (daysLeft < 0) return { label: 'Overdue', color: '#EF4444', bg: '#FEF2F2', border: '#FECACA' };
  if (daysLeft <= DUE_SOON_WINDOW_DAYS) return { label: 'Due soon', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' };
  return { label: 'On track', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' };
};

// ------------------ TRANSFORM FUNCTION ------------------
const transformApiToBoardData = (apiData) => {
  const employees = {};
  const tasks = {};
  const columns = {};
  const columnOrder = [];

  const employeeObj = apiData?.[0] || {};

  Object.entries(employeeObj).forEach(([name, value], index) => {
    const empId = `emp-${value.employee_id}`;

    employees[empId] = {
      id: empId,
      name,
      color: getRandomColor(index),
    };

    columnOrder.push(empId);

    const taskIds = [];

    (value.tasks || []).forEach((task) => {
      // Make task IDs unique by including employee ID to handle same task in multiple columns
      const taskId = `task-${value.employee_id}-${task.task_id}`;

      let status = task.assignment_status;
      // if (task.current_status === 1) status = 'in_progress';
      // if (task.current_status === 2) status = 'completed';

      tasks[taskId] = {
        id: taskId,
        title: task.title,
        status,
        assignee: empId,
        raw: task, // IMPORTANT
      };

      taskIds.push(taskId);
    });

    columns[empId] = {
      id: empId,
      taskIds,
    };
  });

  return { employees, tasks, columns, columnOrder };
};

// ------------------ MAIN COMPONENT ------------------
const TaskBoard = ({ apiResponse, filters }) => {
  const [data, setData] = useState({
    employees: {},
    tasks: {},
    columns: {},
    columnOrder: [],
  });

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { taskdata } = useSelector((state) => state.taskManagement);

  // ------------------ LOAD API ------------------
  useEffect(() => {
    if (taskdata.length > 0) {
      const boardData = transformApiToBoardData(taskdata);
      setData(boardData);
    }
  }, [taskdata]);

  const [selfAssignDialog, setSelfAssignDialog] = useState({
    open: false,
    task: null,
  });

  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    task: null,
  });

  const handleSelfAssign = useCallback((task) => {
    setSelfAssignDialog({
      open: true,
      task,
    });
  }, []);

  const confirmSelfAssign = () => {
    const task = selfAssignDialog.task;
    if (!task) return;

    dispatch(
      updateTaskStatus({
        task_id: task.raw.task_id,
        task_assigned_to: Number(task.assignee.split('-')[1]),
        assignment_status: 0,
      }),
    ).then((res) => {
      if (res.payload?.status === 'success') {
        enqueueSnackbar('Task self assigned successfully!', {
          variant: 'success',
        });
        
        dispatch(getTasks(filters));
        dispatch(getCreatedTasks(filters));
      } else {
        enqueueSnackbar('Failed to self assign task', {
          variant: 'error',
        });
      }

      setSelfAssignDialog({
        open: false,
        task: null,
      });
    });
  };

  const handleViewDetails = useCallback((task) => {
    setDetailsDialog({
      open: true,
      task,
    });
  }, []);

  // Stable per-column task arrays. Rebuilt only when `data` changes (drag / load),
  // so unrelated re-renders (e.g. opening a dialog) don't break Column's React.memo.
  const columnsTasks = useMemo(() => {
    const map = {};
    data.columnOrder.forEach((colId) => {
      const column = data.columns[colId];
      map[colId] = (column?.taskIds || []).map((tid) => data.tasks[tid]);
    });
    return map;
  }, [data]);

  // ------------------ DRAG END ------------------
  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceCol = data.columns[source.droppableId];
    const destCol = data.columns[destination.droppableId];

    const movedTaskId = data.columns[source.droppableId].taskIds[source.index];

    if (sourceCol === destCol) {
      const newTaskIds = Array.from(sourceCol.taskIds);
      const [moved] = newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, moved);

      const newColumn = { ...sourceCol, taskIds: newTaskIds };

      setData({
        ...data,
        columns: { ...data.columns, [newColumn.id]: newColumn },
      });
    } else {
      const sourceTaskIds = Array.from(sourceCol.taskIds);
      const destTaskIds = Array.from(destCol.taskIds);

      sourceTaskIds.splice(source.index, 1);
      destTaskIds.splice(destination.index, 0, movedTaskId);

      const newSourceCol = { ...sourceCol, taskIds: sourceTaskIds };
      const newDestCol = { ...destCol, taskIds: destTaskIds };

      const updatedTask = {
        ...data.tasks[movedTaskId],
        assignee: destCol.id,
      };

      setData({
        ...data,
        tasks: { ...data.tasks, [movedTaskId]: updatedTask },
        columns: {
          ...data.columns,
          [newSourceCol.id]: newSourceCol,
          [newDestCol.id]: newDestCol,
        },
      });

      // ✅ DISPATCH API
      const movedTask = data.tasks[movedTaskId];

      dispatch(
        updateTaskStatus({
          task_id: movedTask.raw.task_id,
          task_assigned_to: Number(destCol.id.split('-')[1]),
          assignment_status: 0,
        }),
      ).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar('Task reassigned successfully!', {
            variant: 'success',
          });
          dispatch(getTasks(filters));
          dispatch(getCreatedTasks(filters));
        } else {
          enqueueSnackbar('Failed to reassign task', {
            variant: 'error',
          });
        }
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: 'auto',
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3.5}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
          }}
        >
          <AssignmentOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#1E293B',
              letterSpacing: '-0.3px',
              fontSize: '1.35rem',
            }}
          >
            Department/Wing Tasks
          </Typography>
          <Typography variant="body2" sx={{ color: '#94A3B8', mt: -0.2, fontSize: '0.82rem' }}>
            Reporting Head — Drag & drop to reassign tasks
          </Typography>
        </Box>
      </Stack>

      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          sx={{
            display: 'flex',
            gap: 2.5,
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': { height: 5 },
            '&::-webkit-scrollbar-track': {
              background: '#F1F5F9',
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E1',
              borderRadius: 3,
            },
          }}
        >
          {data.columnOrder.map((colId) => {
            const column = data.columns[colId];
            const employee = data.employees[colId];
            const tasks = columnsTasks[colId] || [];

            return (
              <Column
                key={colId}
                column={column}
                tasks={tasks}
                employee={employee}
                onSelfAssign={handleSelfAssign}
                onViewDetails={handleViewDetails}
              />
            );
          })}
        </Box>
      </DragDropContext>
      {/* Task Details Dialog (mounted only while open to avoid work on board re-renders) */}
      {detailsDialog.open && detailsDialog.task && (
        <TaskDetailsDialog
          open={detailsDialog.open}
          onClose={() => setDetailsDialog({ open: false, task: null })}
          task={detailsDialog.task}
          filters={filters}
        />
      )}
      {selfAssignDialog.open && selfAssignDialog.task && (
        <TaskAssignmentDialog
          open={selfAssignDialog.open}
          onClose={() => setSelfAssignDialog({ open: false, task: null })}
          task={selfAssignDialog.task}
          confirmSelfAssign={confirmSelfAssign}
          filters={filters}
        />
      )}
    </Box>
  );
};

// ------------------ COLUMN ------------------
// A task counts as "completed" if its status resolves to Completed (id 5) or its
// raw status name contains "Completed" (e.g. "Completed pending from Head").
const isTaskCompleted = (task) => {
  const name = task?.raw?.assignment_status_name || statusById[task?.status]?.label || '';
  return task?.status === 5 || /completed/i.test(name);
};

const Column = React.memo(function Column({ column, tasks, employee, onSelfAssign, onViewDetails }) {
  const completedCount = tasks.filter(isTaskCompleted).length;

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 160px)',
        minWidth: 310,
        maxWidth: 320,
        flex: '0 0 310px',
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 3,
        p: 2,
        transition: 'all 0.25s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          borderColor: '#CBD5E1',
        },
      }}
    >
      {/* Colored top accent (matches stat card gradient strip) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${employee.color} 0%, ${employee.color}88 100%)`,
        }}
      />

      {/* Column Header (stays pinned while task list scrolls) */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          flexShrink: 0,
          mb: 2,
          pb: 2,
          borderBottom: '1px solid #F1F5F9',
          background: '#fff',
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            background: `linear-gradient(135deg, ${employee.color} 0%, ${employee.color}CC 100%)`,
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            boxShadow: `0 4px 10px ${employee.color}40`,
          }}
        >
          {employee.name[0]}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#1E293B',
              fontSize: '0.9rem',
              lineHeight: 1.2,
            }}
          >
            {employee.name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.72rem', display: 'block' }}>
            {tasks.length} task{tasks.length !== 1 ? 's' : ''} assigned
          </Typography>
          {tasks.length > 0 && (
            <Chip
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
              label={`${completedCount}/${tasks.length} completed`}
              size="small"
              sx={{
                mt: 0.6,
                height: 26,
                fontSize: '0.74rem',
                fontWeight: 700,
                borderRadius: '7px',
                color: '#fff',
                border: 'none',
                background:
                  completedCount === tasks.length
                    ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)'
                    : 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                boxShadow:
                  completedCount === tasks.length
                    ? '0 2px 8px rgba(34, 197, 94, 0.3)'
                    : '0 2px 8px rgba(99, 102, 241, 0.3)',
                '& .MuiChip-icon': { color: '#fff !important', ml: '7px' },
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: `${employee.color}10`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 16, color: employee.color }} />
        </Box>
      </Stack>

      {/* Droppable Area */}
      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              flex: 1,
              minHeight: 70,
              overflowY: 'auto',
              overflowX: 'hidden',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              p: snapshot.isDraggingOver ? 1 : 0,
              pr: 0.5,
              background: snapshot.isDraggingOver ? '#F8FAFC' : 'transparent',
              border: snapshot.isDraggingOver ? '2px dashed #CBD5E1' : '2px dashed transparent',
              '&::-webkit-scrollbar': { width: 5 },
              '&::-webkit-scrollbar-track': { background: '#F1F5F9', borderRadius: 3 },
              '&::-webkit-scrollbar-thumb': { background: '#CBD5E1', borderRadius: 3 },
            }}
          >
            {tasks.map((task, index) => (
              <TaskCard
                key={task.id}
                task={task}
                employee={employee}
                index={index}
                onSelfAssign={onSelfAssign}
                onViewDetails={onViewDetails}
              />
            ))}
            {provided.placeholder}
            {tasks.length === 0 && !snapshot.isDraggingOver && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 3.5,
                }}
              >
                <AssignmentOutlinedIcon sx={{ fontSize: 28, color: '#CBD5E1', mb: 0.5 }} />
                <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.8rem' }}>
                  No tasks assigned
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Droppable>
    </Box>
  );
});

// ------------------ TASK CARD ------------------
const TaskCard = React.memo(function TaskCard({ task, index, onSelfAssign, onViewDetails, employee }) {
  // Fallback to Pending config so an unknown status never crashes cloneElement.
  const config = statusById[task?.status] || statusById[0];

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('cred'))?.profile || {};
    } catch {
      return {};
    }
  }, []);

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          elevation={0}
          sx={{
            mb: 1.5,
            background: snapshot.isDragging ? '#FFFFFF' : '#FAFBFC',
            border: snapshot.isDragging ? '1px solid #A5B4FC' : '1px solid #E2E8F0',
            borderRadius: 2.5,
            cursor: 'grab',
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'visible',
            transform: snapshot.isDragging ? 'rotate(1.5deg)' : 'none',
            boxShadow: snapshot.isDragging
              ? '0 12px 28px rgba(99, 102, 241, 0.15), 0 4px 10px rgba(0,0,0,0.08)'
              : 'none',
            '&:hover': {
              background: '#FFFFFF',
              borderColor: '#CBD5E1',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              transform: 'translateY(-1px)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3.5,
              height: '50%',
              borderRadius: '0 3px 3px 0',
              background: config?.dot,
              opacity: 0.8,
            },
          }}
        >
          <CardContent sx={{ p: '14px 14px 14px 18px !important' }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#1E293B',
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                    mb: 1.2,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {task.title}
                </Typography>

                {/* Badges: deadline, status, subtask completion, PMS origin, tags -
                    a real flex row so every badge lines up on the same
                    vertical center, instead of relying on inline text flow
                    and per-chip margins. */}
                <Stack direction="row" flexWrap="wrap" alignItems="center" useFlexGap spacing={0.75} sx={{ mb: 1 }}>
                  {/* Deadline */}
                  {task.raw.expected_delivery_date &&
                    (() => {
                      const urgency = getDeadlineUrgency(task.raw.expected_delivery_date, task.raw.assignment_status);
                      return (
                        <Chip
                          icon={<EventIcon sx={{ fontSize: '13px !important' }} />}
                          label={`${dayjs(task.raw.expected_delivery_date).format('DD MMM')} · ${urgency.label}`}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            background: urgency.bg,
                            color: urgency.color,
                            border: `1px solid ${urgency.border}`,
                            borderRadius: '6px',
                            '& .MuiChip-icon': { color: `${urgency.color} !important`, ml: '5px' },
                            '& .MuiChip-label': { px: 0.8 },
                          }}
                        />
                      );
                    })()}

                  {/* Status Chip */}
                  <Chip
                    icon={React.cloneElement(config?.icon, {
                      sx: {
                        fontSize: '13px !important',
                        color: `${config?.color} !important`,
                      },
                    })}
                    label={config?.label}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      background: config?.bg,
                      color: config?.color,
                      border: `1px solid ${config?.border}`,
                      borderRadius: '6px',
                      '& .MuiChip-icon': {
                        ml: '5px',
                      },
                      '& .MuiChip-label': {
                        px: 0.8,
                      },
                    }}
                  />

                  {/* Subtask completion */}
                  {Number(task.raw.total_subtasks) > 0 &&
                    (() => {
                      const subtasksDone = Number(task.raw.completed_subtasks) === Number(task.raw.total_subtasks);
                      const subtaskColor = subtasksDone ? '#16A34A' : '#64748B';
                      return (
                        <Chip
                          icon={<ChecklistIcon sx={{ fontSize: '13px !important', color: `${subtaskColor} !important` }} />}
                          label={`${task.raw.completed_subtasks}/${task.raw.total_subtasks} subtasks`}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            background: subtasksDone ? '#F0FDF4' : '#F1F5F9',
                            color: subtaskColor,
                            border: '1px solid',
                            borderColor: subtasksDone ? '#DCFCE7' : '#E2E8F0',
                            borderRadius: '6px',
                            '& .MuiChip-icon': { ml: '5px' },
                            '& .MuiChip-label': { px: 0.8 },
                          }}
                        />
                      );
                    })()}

                  {/* PMS origin - this task was auto-created from a PMS goal job */}
                  {task.raw.pms_link_origin === 'job_to_task' && (
                    <Chip
                      icon={<FlagIcon sx={{ fontSize: '13px !important' }} />}
                      label="From PMS"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        background: '#ede7f6',
                        color: '#4527a0',
                        border: '1px solid #d1c4e9',
                        borderRadius: '6px',
                        '& .MuiChip-icon': { color: '#4527a0 !important', ml: '5px' },
                        '& .MuiChip-label': { px: 0.8 },
                      }}
                    />
                  )}

                  {/* Tags (cross-module - shared with PMS goals) */}
                  {Array.isArray(task.raw.tags) &&
                    task.raw.tags.map((tag) => (
                      <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          background: `${tag.color}22`,
                          color: tag.color,
                          border: `1px solid ${tag.color}55`,
                          borderRadius: '6px',
                          '& .MuiChip-label': { px: 0.8 },
                        }}
                      />
                    ))}
                </Stack>

                {/* Created By & Assigned To */}
                <Stack spacing={0.5} mt={1.2}>
                  {task.raw.created_by_name && (
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: '#94A3B8',
                          minWidth: 70,
                        }}
                      >
                        Created by
                      </Typography>
                      <Chip
                        label={`${task.raw.created_by_name} - ${task.raw.source_dept_name}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.68rem',
                          fontWeight: 500,
                          background: '#EEF2FF',
                          color: '#4F46E5',
                          border: '1px solid #E0E7FF',
                          borderRadius: '5px',
                          '& .MuiChip-label': { px: 0.8, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
                        }}
                      />
                    </Stack>
                  )}
                  {task.raw.assigned_to_name && (
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: '#94A3B8',
                          minWidth: 70,
                        }}
                      >
                        Assigned to
                      </Typography>
                      <Chip
                        label={`${task.raw.assigned_to_name} - ${task.raw.target_dept_name}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.68rem',
                          fontWeight: 500,
                          background: '#F0FDF4',
                          color: '#16A34A',
                          border: '1px solid #DCFCE7',
                          borderRadius: '5px',
                          '& .MuiChip-label': { px: 0.8, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
                        }}
                      />
                    </Stack>
                  )}
                </Stack>

                <Stack direction="row" spacing={1} mt={1.5}>
                  {currentUser?.name === employee.name && (
                    <Tooltip title="Assign">
                      <Button
                        size="small"
                        fullWidth
                        onClick={() => onSelfAssign(task)}
                        variant="contained"
                        startIcon={<PersonAddAlt1Outlined fontSize="small" />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          borderRadius: 2,
                          boxShadow: 'none',
                          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5558E6 0%, #7C4FE0 100%)',
                            boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
                          },
                        }}
                      >
                        Assign
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="View Details">
                    <Button
                      size="small"
                      fullWidth
                      variant="outlined"
                      disabled={!task.raw}
                      onClick={() => onViewDetails(task)}
                      startIcon={<VisibilityOutlined fontSize="small" />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        borderColor: '#E2E8F0',
                        color: '#475569',
                        '&:hover': { borderColor: '#CBD5E1', background: '#F8FAFC' },
                      }}
                    >
                      View
                    </Button>
                  </Tooltip>
                </Stack>
              </Box>

              <DragIndicatorIcon
                sx={{
                  fontSize: 18,
                  color: '#CBD5E1',
                  mt: 0.2,
                  transition: 'color 0.2s',
                  '&:hover': { color: '#94A3B8' },
                }}
              />
            </Stack>
          </CardContent>
        </Card>
      )}
    </Draggable>
    // ========================== dialogs =============================
  );
});

export default TaskBoard;
